// #16 Field, #17 Input and TextArea. A Field is the label row (his field name, *Required* / *Optional*, a labelSlot for
// row 110's state pill (FieldState)), one control, the hint and the validation error. The control inside it (Input, TextArea, Select,
// or a group's own control through useFieldControl) takes its id, describedby, aria-invalid, aria-required, blocked
// reason and mode from the Field, so the wiring is written once.
// Error (DS §2.1): the control's edge in --red-text, an alert-icon hint in --red-text under it, aria-invalid and
// aria-describedby. Blocked (DC-24): a native text field is readOnly + aria-disabled with the inline .mc-sr reason,
// so typing is impossible and the reason is read. mode="view": the value as text, no control.

import {
  createContext, forwardRef, useContext, useId, type InputHTMLAttributes, type ReactElement, type ReactNode, type TextareaHTMLAttributes,
} from "react";
import { COMMON_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useHs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useBlocked, type Blocked, type ControlProps } from "./Button";
import { Icon, type IconName } from "./icons";

const NONE = "—";

/** What a Field hands its control. */
export interface FieldContextValue {
  id: string;
  describedBy?: string | undefined;
  invalid: boolean;
  required: boolean;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldControlOptions {
  id?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  "aria-describedby"?: string | undefined;
}

/**
 * A control's wiring, merged from its own props and the enclosing Field: the id, the describedby list (hint, error
 * and the blocked reason), the aria-* props to spread, the blocked reason node to render as a sibling, and the mode.
 */
export function useFieldControl(own: FieldControlOptions = {}) {
  const field = useContext(FieldContext);
  const fallback = useId();
  const blocked = own.blocked ?? field?.blocked;
  const describedBy = [field?.describedBy, own["aria-describedby"]].filter(Boolean).join(" ") || undefined;
  const block = useBlocked(blocked, describedBy);
  const invalid = Boolean(own.invalid ?? field?.invalid);
  const required = Boolean(own.required ?? field?.required);
  return {
    id: own.id ?? field?.id ?? fallback,
    blocked,
    invalid,
    mode: own.mode ?? field?.mode,
    inField: field !== null,
    /** The enclosing Field's label element id (a listbox or group names itself by it); undefined outside a Field. */
    labelId: field ? `${field.id}-l` : undefined,
    reason: block.reason,
    aria: {
      "aria-describedby": block.describedBy || undefined,
      "aria-invalid": invalid ? true : undefined,
      "aria-required": required ? true : undefined,
      ...block.props,
    },
  };
}

export interface FieldProps {
  /** His field name (G §5–§12), from a labels module. */
  label: ReactNode;
  /** The control: an Input, TextArea, Select, or a control using useFieldControl. */
  children: ReactNode;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  hint?: ReactNode | undefined;
  /** "flag": the orange, bold hint with an alert icon (alt .hint.flag). */
  hintTone?: "flag" | undefined;
  /** The validation error sentence. */
  error?: ReactNode | undefined;
  /** After the label: row 110's state pill (FieldState). */
  labelSlot?: ReactNode | undefined;
  blocked?: Blocked | undefined;
  /** "view": the label and the value as text. */
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  /** The control's id (default generated). */
  id?: string | undefined;
  className?: string | undefined;
}

export function Field({ label, children, required, optional, hint, hintTone, error, labelSlot, blocked, mode, can, id, className }: FieldProps) {
  const generated = useId();
  const controlId = id ?? `${generated}-c`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-err`;
  if (can === false) return null;
  const view = mode === "view";
  const hasError = error !== undefined && error !== null && error !== false && !view;
  const describedBy = [hint ? hintId : null, hasError ? errorId : null].filter(Boolean).join(" ") || undefined;
  const value: FieldContextValue = { id: controlId, describedBy, invalid: hasError, required: Boolean(required), blocked, mode };
  const words = (
    <>
      {label}
      {required && !view ? <span className="mc-req">{COMMON_LABELS.required}</span> : null}
      {optional && !view ? <span className="mc-opt">{COMMON_LABELS.optional}</span> : null}
    </>
  );
  return (
    <div className={cx("mc-field", hasError && "mc-field-err", view && "mc-field-view", className)}>
      <div className="mc-fl">
        {view ? <span id={`${controlId}-l`}>{words}</span> : <label id={`${controlId}-l`} htmlFor={controlId}>{words}</label>}
        {labelSlot ?? null}
      </div>
      <FieldContext.Provider value={value}>{children}</FieldContext.Provider>
      {hint && !view ? (
        <p className={cx("mc-hint", hintTone === "flag" && "mc-hint-flag")} id={hintId}>
          {hintTone === "flag" ? <Icon name="alert" size={14} /> : null}
          {hint}
        </p>
      ) : null}
      {hasError ? <p className="mc-hint mc-hint-err" id={errorId}><Icon name="alert" size={14} />{error}</p> : null}
    </div>
  );
}

/** The value of a field in view mode (or a blocked select): the text, or "—". */
function shown(value: unknown): ReactNode {
  if (value === undefined || value === null || value === "") return NONE;
  return Array.isArray(value) ? value.join(", ") : String(value);
}

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, "style" | "className" | "size" | "disabled" | "children" | "readOnly">;

export interface InputProps extends ControlProps, NativeInput {
  /** 34 px (default 40). */
  sm?: boolean | undefined;
  mono?: boolean | undefined;
  /** A leading icon (search, calendar …). */
  icon?: IconName | undefined;
  /** A validation error outside a Field (inside one, the Field's error sets it). */
  invalid?: boolean | undefined;
  /** A plain read-only value (not a rule block). */
  readOnly?: boolean | undefined;
  /** Standalone use: the field name, drawn as a Field around the input. */
  label?: ReactNode | undefined;
  className?: string | undefined;
}

/** Wraps a field control in the .mc-inp box; a blocked control gets the mirrored tooltip and its inline reason. */
function Box(props: { className: string; blocked: Blocked | undefined; reason: ReactNode; icon?: IconName | undefined; children: ReactElement }) {
  const box = (
    <span className={props.className}>
      {props.icon ? <Icon name={props.icon} size={16} tone="muted" /> : null}
      {props.children}
    </span>
  );
  if (!props.blocked) return box;
  return (
    <>
      <Tooltip label={props.blocked.reason} mode="mirror">{box}</Tooltip>
      {props.reason}
    </>
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const { label, can, ...rest } = props;
  if (can === false) return null;
  if (label !== undefined && rest.mode !== "view") return <Field label={label} id={rest.id} blocked={rest.blocked}><InputControl {...rest} ref={ref} /></Field>;
  if (label !== undefined) return <Field label={label} mode="view"><InputControl {...rest} ref={ref} /></Field>;
  return <InputControl {...rest} ref={ref} />;
});

const InputControl = forwardRef<HTMLInputElement, Omit<InputProps, "label" | "can">>(function InputControl(
  { blocked, mode, hs, sm, mono, icon, invalid, readOnly, className, id, type = "text", "aria-describedby": describedBy, ...rest },
  ref,
) {
  const hot = useHs(hs);
  const control = useFieldControl({ id, invalid, blocked, mode, "aria-describedby": describedBy, required: rest.required });
  if (control.mode === "view") {
    return <span className={cx("mc-field-v", mono && "mc-mono")}>{shown(rest.value ?? rest.defaultValue)}</span>;
  }
  return (
    <Box
      className={cx("mc-inp", sm && "mc-inp-sm", mono && "mc-inp-mono", control.invalid && "mc-inp-err", control.blocked && "mc-dis", className)}
      blocked={control.blocked}
      reason={control.reason}
      icon={icon}
    >
      <input {...rest} {...control.aria} {...hot} ref={ref} id={control.id} type={type} className="mc-inp-c" readOnly={Boolean(control.blocked) || readOnly} />
    </Box>
  );
});

type NativeTextArea = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "style" | "className" | "disabled" | "children" | "readOnly">;

export interface TextAreaProps extends ControlProps, NativeTextArea {
  sm?: boolean | undefined;
  mono?: boolean | undefined;
  invalid?: boolean | undefined;
  readOnly?: boolean | undefined;
  label?: ReactNode | undefined;
  className?: string | undefined;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(props, ref) {
  const { label, can, ...rest } = props;
  if (can === false) return null;
  if (label !== undefined) {
    return <Field label={label} id={rest.id} blocked={rest.blocked} mode={rest.mode}><TextAreaControl {...rest} ref={ref} /></Field>;
  }
  return <TextAreaControl {...rest} ref={ref} />;
});

const TextAreaControl = forwardRef<HTMLTextAreaElement, Omit<TextAreaProps, "label" | "can">>(function TextAreaControl(
  { blocked, mode, hs, sm, mono, invalid, readOnly, className, id, rows = 4, "aria-describedby": describedBy, ...rest },
  ref,
) {
  const hot = useHs(hs);
  const control = useFieldControl({ id, invalid, blocked, mode, "aria-describedby": describedBy, required: rest.required });
  if (control.mode === "view") {
    return <span className={cx("mc-field-v", "mc-field-v-ta", mono && "mc-mono")}>{shown(rest.value ?? rest.defaultValue)}</span>;
  }
  return (
    <Box
      className={cx("mc-inp", "mc-inp-ta", sm && "mc-inp-sm", mono && "mc-inp-mono", control.invalid && "mc-inp-err", control.blocked && "mc-dis", className)}
      blocked={control.blocked}
      reason={control.reason}
    >
      <textarea {...rest} {...control.aria} {...hot} ref={ref} id={control.id} rows={rows} className="mc-inp-c" readOnly={Boolean(control.blocked) || readOnly} />
    </Box>
  );
});

export { NONE as FIELD_NONE, shown as fieldValueText };
